import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Paper, Chip, Grid, Button, TextField, IconButton, Autocomplete } from "@mui/material";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SelectableRadioGroup from "../../../../common/selectableRadioGroup/selectable-radio-group";
import axios from "axios";

const issues = ["Anxiety", "Stress", "Bereavement", "Anger", "Frustration", "Fear", "Homesick", "Other"];
const triggers = ["Comment", "Denial", "Request", "Argument", "Touch", "Teasing", "Disruption", "Conflict", "Other"];
const warningSigns = ["No signs", "Pacing", "Fidgeting", "Clenched", "Silence", "Shouting", "Tension", "Other"];

const ChallengingBehavior = ({ updateFormData, nextStep, prevStep, formData, temp, volume, person }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [selectedTrigger, setSelectedTrigger] = useState("");
  const [selectedIssue, setSelectedIssue] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [note, setNote] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [incidentRecords, setIncidentRecords] = useState([]); // Stores submitted voice records
  const [selectedWarningSign, setSelectedWarningSign] = useState("");
  const [temperature, setTemperature] = useState(temp || "");
  const [sound, setSound] = useState(volume || "");
  const [whoWasAround, setWhoWasAround] = useState(person || "");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (formData && formData.length > 0) {
      setIncidentRecords(formData);
    }
  }, [formData]);

  const allowedBehaviors = Array.isArray(incidentRecords) ? incidentRecords.map((b) => b.behavior) : [];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        // Filter users with roleid === 4 and update state
        const filteredUsers = response.data.filter((usera) => usera.id !== user?.id);
        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchUsers();
  }, [user?.id]);

  // Start Recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    };

    mediaRecorder.start();
    setRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const newBehavior = selectedIssue ? selectedIssue : selectedTrigger ? selectedTrigger : selectedWarningSign;
  // Handle Submit - Save Recorded Entry
  const handleSubmit = async () => {
    // Check for duplicates
    const isDuplicate = incidentRecords.some((record) => record.behavior === newBehavior);
    if (isDuplicate) {
      alert("This behavior has already been recorded.");
      return;
    }

    let uploadedAudioUrl = null;

    try {
      // Upload audio if available
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const { audioUrl } = await uploadRes.json();
        uploadedAudioUrl = audioUrl;
      }

      // Optional: Prevent submission if both audio and note are empty
      if (!uploadedAudioUrl && !note.trim()) {
        alert("Please provide at least a note or an audio recording.");
        return;
      }

      // Add new record to state
      const newRecord = {
        behavior: newBehavior,
        audio: uploadedAudioUrl,
        note: note,
      };

      setIncidentRecords([...incidentRecords, newRecord]);
      setDialogOpen(false);
      setAudioURL(null);
      setNote("");
      setSelectedIssue(""); // Reset criteria
      setSelectedTrigger(""); // Reset behavior
      setSelectedWarningSign(""); // Reset warning sign
    } catch (err) {
      console.error("Upload or submission failed", err);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setNote("");
    setAudioURL(null);
    setSelectedIssue(""); // Reset criteria after submission
    setSelectedTrigger(""); // Reset behavior after submission
    setSelectedWarningSign("");
  };

  // Handle Deleting a Record
  const handleDeleteRecord = (index) => {
    const updatedRecords = incidentRecords.filter((_, i) => i !== index);
    setIncidentRecords(updatedRecords);
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Incident Management</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8} md={8}>
          <Typography sx={{ fontSize: "16px", color: "black", fontWeight: "bold", mb: 2 }}>
            Challenging Behavior
          </Typography>
          <Typography sx={{ fontSize: "14px", mb: 3 }}>
            Describe the events leading up to the challenging behavior – including the underlying issues, triggers,
            temperature, sounds, who was around and warning signs as well as the immediate precipitating events
          </Typography>

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mb: 2 }}>Issues</Typography>

          <SelectableRadioGroup
            options={issues}
            selectedValue={selectedIssue}
            setSelectedValue={setSelectedIssue}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mb: 2, mt: 3 }}>Triggers</Typography>

          <SelectableRadioGroup
            options={triggers}
            selectedValue={selectedTrigger}
            setSelectedValue={setSelectedTrigger}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          {/* Warning Signs */}
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mb: 2, mt: 3 }}>
            Warning Signs *
          </Typography>

          <SelectableRadioGroup
            options={warningSigns}
            selectedValue={selectedWarningSign}
            setSelectedValue={setSelectedWarningSign}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <Grid container spacing={2}>
            <Grid item lg={6} md={6}>
              {/* Temperature */}
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>
                Environmental Factors
              </Typography>
              <TextField
                fullWidth
                variant='outlined'
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item lg={6} md={6}>
              {/* Temperature */}
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>Sounds</Typography>
              <Autocomplete
                id='sound'
                options={["Loud", "Quite", "Very noisy", "Very quite"]}
                value={sound}
                onChange={(event, newValue) => {
                  setSound(newValue);
                }}
                renderInput={(params) => <TextField {...params} placeholder='Select the sound' />}
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Who was Around */}
          <Grid container>
            <Grid item lg={6} md={6}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black" }}>Who was Around *</Typography>
              <Autocomplete
                id='name'
                options={users}
                getOptionLabel={(option) => option?.username || ""} // ✅ Display username
                value={users.find((u) => u.username === whoWasAround) || null} // ✅ Maintain object for proper matching
                onChange={(event, newValue) => {
                  setWhoWasAround(newValue?.username || ""); // ✅ Store just username
                }}
                renderInput={(params) => <TextField {...params} placeholder='Select a user' />}
                fullWidth
              />
            </Grid>
          </Grid>

          <ReusableDialog
            isOpen={dialogOpen}
            onClose={handleClose}
            title={newBehavior}
            onSubmit={handleSubmit}
            submitText='Submit'
            cancelText='Cancel'
            disable={!audioURL && !note}
          >
            {/* Description */}
            <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
              Describe how they are putting themselves at risk of injury.
            </Typography>

            {/* Audio Recording UI */}
            <Box
              sx={{
                border: "2px dashed #ccc",
                padding: 3,
                textAlign: "center",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
                mb: 2,
              }}
            >
              {!audioURL ? (
                <>
                  <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>
                    Try Recording Your Sound Now
                  </Typography>
                  <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
                  <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                    To start recording press the <b>“Start Recording”</b> button and start recording!
                  </Typography>
                  <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                    {recording ? "Stop Recording" : "Start Recording"}
                  </Button>
                </>
              ) : (
                <>
                  <audio controls src={audioURL} />
                  <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                    Record Again
                  </Button>
                </>
              )}
            </Box>

            {/* Notes Field */}
            <Typography sx={{ fontSize: "14px", mb: 1 }}>If any note,</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder='Enter your note here...'
              variant='outlined'
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </ReusableDialog>
        </Grid>
        <Grid item lg={4} md={4}>
          {/* Incident Management Details Section - Recorded Entries */}
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
              Incident Management
            </Typography>

            {incidentRecords.length > 0 ? (
              <>
                {[
                  { title: "Issues", data: issues },
                  { title: "Triggers", data: triggers },
                  { title: "Warning Signs", data: warningSigns },
                ].map((category, index) => {
                  const filteredRecords = incidentRecords.filter((record) => category.data.includes(record.behavior));

                  if (filteredRecords.length === 0) return null;

                  return (
                    <Box key={index} sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: 1, color: "black" }}>
                        {category.title}
                      </Typography>
                      {filteredRecords.map((record, recordIndex) => (
                        <Paper
                          key={recordIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 1,
                            mb: 1,
                            borderRadius: 2,
                            border: "1px solid #ccc",
                          }}
                        >
                          <Typography sx={{ fontSize: "14px", color: "black" }}>
                            {record.audio ? (
                              record.behavior
                            ) : (
                              <>
                                {record.behavior}
                                <br />
                                {record.note}
                              </>
                            )}
                          </Typography>

                          <Box>
                            {record.audio && (
                              <>
                                <IconButton onClick={() => window.open(record.audio, "_blank")}>
                                  <MicIcon color='primary' />
                                </IconButton>
                                <IconButton onClick={() => window.open(record.audio, "_blank")}>
                                  <CloudDownloadIcon color='primary' />
                                </IconButton>
                              </>
                            )}
                            <IconButton onClick={() => handleDeleteRecord(index)}>
                              <DeleteIcon color='error' />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  );
                })}
              </>
            ) : (
              <Typography sx={{ fontSize: "14px", color: "gray" }}>No incident records found.</Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            prevStep();
          }}
        >
          Previous
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            nextStep(); // Call the function properly
            updateFormData({ challengingInfo: incidentRecords, sound, temperature, whoWasAround }); // Call the function properly
          }}
          disabled={
            !incidentRecords.some((r) => issues.includes(r.behavior)) ||
            !incidentRecords.some((r) => triggers.includes(r.behavior)) ||
            !incidentRecords.some((r) => warningSigns.includes(r.behavior)) ||
            sound === "" ||
            temperature === "" ||
            whoWasAround === ""
          }
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default ChallengingBehavior;
